/**
 * Client-side avatar cache keyed by userId + avatar_s3_key.
 * Presigned download URLs rotate; we only re-fetch when the s3 key changes.
 */

const DB_NAME = 'contracting-avatar-cache';
const DB_VERSION = 1;
const STORE_NAME = 'avatars';

/** In-memory: userId -> { key, objectUrl } */
const memoryCache = new Map();

const openDb = () =>
  new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB unavailable'));
      return;
    }
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => reject(request.error || new Error('Failed to open avatar cache'));
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'userId' });
      }
    };
  });

const idbGet = async (userId) => {
  try {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const req = tx.objectStore(STORE_NAME).get(userId);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => reject(req.error);
    });
  } catch {
    return null;
  }
};

const idbSet = async (userId, key, blob) => {
  try {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).put({ userId, key, blob });
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    // Cache write is best-effort
  }
};

const idbDelete = async (userId) => {
  try {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).delete(userId);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    // best-effort
  }
};

const revokeMemory = (userId) => {
  const cached = memoryCache.get(userId);
  if (cached?.objectUrl) {
    URL.revokeObjectURL(cached.objectUrl);
  }
  memoryCache.delete(userId);
};

/**
 * Persist a local File/Blob under the given s3 key (e.g. right after upload).
 * @returns {string} object URL for immediate display
 */
export const cacheAvatarBlob = async (userId, s3Key, blob) => {
  if (!userId || !s3Key || !blob) return null;
  revokeMemory(userId);
  await idbSet(userId, s3Key, blob);
  const objectUrl = URL.createObjectURL(blob);
  memoryCache.set(userId, { key: s3Key, objectUrl });
  return objectUrl;
};

/** Clear cached avatar for a user (after delete or logout optional cleanup). */
export const clearAvatarCache = async (userId) => {
  if (!userId) return;
  revokeMemory(userId);
  await idbDelete(userId);
};

/**
 * Resolve a displayable avatar URL for the user.
 * Hits memory → IndexedDB → fetchDownloadUrl() only on cache miss / key change.
 *
 * @param {string} userId
 * @param {string|null|undefined} s3Key
 * @param {() => Promise<string>} fetchDownloadUrl - returns a short-lived presigned URL
 * @returns {Promise<string|null>} object URL or null
 */
export const resolveAvatarSrc = async (userId, s3Key, fetchDownloadUrl) => {
  if (!userId || !s3Key) return null;

  const mem = memoryCache.get(userId);
  if (mem?.key === s3Key && mem.objectUrl) {
    return mem.objectUrl;
  }

  const stored = await idbGet(userId);
  if (stored?.key === s3Key && stored.blob) {
    revokeMemory(userId);
    const objectUrl = URL.createObjectURL(stored.blob);
    memoryCache.set(userId, { key: s3Key, objectUrl });
    return objectUrl;
  }

  const downloadUrl = await fetchDownloadUrl();
  if (!downloadUrl) return null;

  const response = await fetch(downloadUrl);
  if (!response.ok) {
    throw new Error(`Avatar download failed (${response.status})`);
  }
  const blob = await response.blob();
  return cacheAvatarBlob(userId, s3Key, blob);
};
